import React, { useEffect, useRef, useState } from 'react';
import CreditoBzR from './CreditoBzR';

// Folga para considerar que a pessoa chegou ao fim da página: o último
// pixel exato quase nunca é atingido (zoom, barra de rolagem fracionada).
const FOLGA_FIM = 24;

/**
 * Rodapé das telas internas com o crédito da BzR.
 *
 * Não pode chamar atenção — com o Bloco piscando, à vista o tempo todo ele
 * disputava o olhar com a tela de trabalho. Por isso:
 *
 * - Página que cabe inteira na tela (sem rolagem): o rodapé não aparece.
 *   Ali ele ficaria sempre à vista, logo abaixo do conteúdo.
 * - Página com rolagem: o rodapé ocupa o lugar dele no fim, mas só se
 *   mostra quando a pessoa rola até lá.
 *
 * "Cabe na tela" é medido sem o próprio rodapé: senão a altura dele mesmo
 * podia criar a rolagem que o faz aparecer.
 */
function RodapeBzR({ darkMode }) {
  const rodapeRef = useRef(null);
  const [temRolagem, setTemRolagem] = useState(false);
  const [noFim, setNoFim] = useState(false);

  useEffect(() => {
    let quadro = null;

    const medir = () => {
      quadro = null;
      const doc = document.documentElement;
      const rodape = rodapeRef.current;
      // Fora do fluxo (display: none) o rodapé tem altura 0; à vista, a
      // altura dele mais a margem de cima sai da conta.
      const alturaRodape = rodape && rodape.offsetParent !== null
        ? rodape.offsetHeight + parseFloat(getComputedStyle(rodape).marginTop || 0)
        : 0;
      const alturaConteudo = doc.scrollHeight - alturaRodape;
      setTemRolagem(alturaConteudo > window.innerHeight + 1);
      setNoFim(window.scrollY + window.innerHeight >= doc.scrollHeight - FOLGA_FIM);
    };

    // Rolagem e redimensionamento disparam em rajada: uma medida por quadro.
    const agendar = () => {
      if (quadro === null) quadro = requestAnimationFrame(medir);
    };

    medir();
    window.addEventListener('scroll', agendar, { passive: true });
    window.addEventListener('resize', agendar);
    // O conteúdo chega depois (buscas, troca de aba, troca de rota): a
    // altura da página muda sem rolagem nem redimensionamento da janela.
    const observador = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(agendar)
      : null;
    observador?.observe(document.body);

    return () => {
      window.removeEventListener('scroll', agendar);
      window.removeEventListener('resize', agendar);
      observador?.disconnect();
      if (quadro !== null) cancelAnimationFrame(quadro);
    };
  }, []);

  const visivel = temRolagem && noFim;

  return (
    <footer
      ref={rodapeRef}
      aria-hidden={!visivel}
      className={`mt-10 pt-4 pb-2 transition-opacity duration-500 ${
        temRolagem ? '' : 'hidden'
      } ${visivel ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      <CreditoBzR darkMode={darkMode} />
    </footer>
  );
}

export default RodapeBzR;
